/**
 * Flow Optimizer — weight-rule helpers (pure, provider-agnostic).
 *
 * `parseRuleWeightConfig` is the read-boundary shape guard (mirrors
 * `parseCapacityRules`); `weightRulesIssue` is the BLOCKING check enforced in
 * both the editor and the API (invalid payloads never save); and
 * `weightRulesWarning` is the advisory author-time check — static totals that
 * COULD collide are only a warning here because the rules may match disjoint
 * pools at runtime; the engine does the authoritative per-pool block.
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  ExcludeRulesTree,
  RuleNode,
  RuleWeightConfig,
} from "./voluum-flow-optimizer.types";

export const WEIGHT_RULE_PERCENT_MIN = 1;
export const WEIGHT_RULE_PERCENT_MAX = 100;

type RuleGroup = Extract<RuleNode, { kind: "group" }>;

const LAYERS = ["paths", "landers", "offers"] as const;

/** Coerce a persisted `weight` payload; undefined for anything malformed. */
export function parseRuleWeightConfig(
  value: unknown,
): RuleWeightConfig | undefined {
  if (!value || typeof value !== "object") return undefined;
  const { mode, percent } = value as Partial<RuleWeightConfig>;
  if (mode !== "static" && mode !== "dynamic") return undefined;
  if (typeof percent !== "number" || !Number.isInteger(percent))
    return undefined;
  if (percent < WEIGHT_RULE_PERCENT_MIN || percent > WEIGHT_RULE_PERCENT_MAX)
    return undefined;
  return { mode, percent };
}

/** Pre-order walk: every group in the tree carrying the `'weight'` action. */
export function collectWeightGroups(node: RuleNode | null | undefined): RuleGroup[] {
  if (!node) return [];
  if (node.kind === "not") return collectWeightGroups(node.child);
  if (node.kind !== "group") return [];
  const self = node.actions?.includes("weight") ? [node] : [];
  return [...self, ...node.children.flatMap((c) => collectWeightGroups(c))];
}

const groupLabel = (g: RuleGroup): string => g.name?.trim() || "Weight rule";

/** The blocking problem in ONE group's weight payload, or null. */
function groupWeightIssue(group: RuleGroup): string | null {
  if (parseRuleWeightConfig(group.weight)) return null;
  const percent = (group.weight as Partial<RuleWeightConfig> | undefined)?.percent;
  if (typeof percent === "number") {
    return (
      `${groupLabel(group)} — the weight percent must be a whole number ` +
      `between ${WEIGHT_RULE_PERCENT_MIN} and ${WEIGHT_RULE_PERCENT_MAX}.`
    );
  }
  return `${groupLabel(group)} — the Weight action needs a mode and a percent.`;
}

/**
 * First BLOCKING weight-rule problem in the tree, or null. Enforced in both
 * the editor (Save gate) and the API (DTO validator) so they cannot disagree.
 */
export function weightRulesIssue(
  tree: ExcludeRulesTree | undefined | null,
): string | null {
  for (const layer of LAYERS) {
    for (const group of collectWeightGroups(tree?.[layer])) {
      const issue = groupWeightIssue(group);
      if (issue) return issue;
    }
  }
  return null;
}

/**
 * Advisory author-time warning: a layer whose STATIC totals sum over 100%
 * would be blocked by the engine if those rules ever matched in the same
 * pool. Not blocking — the rules may legitimately target disjoint pools.
 */
export function weightRulesWarning(
  tree: ExcludeRulesTree | undefined | null,
): string | null {
  for (const layer of LAYERS) {
    const statics = collectWeightGroups(tree?.[layer]).filter(
      (g) => parseRuleWeightConfig(g.weight)?.mode === "static",
    );
    const total = statics.reduce((sum, g) => sum + (g.weight?.percent ?? 0), 0);
    if (statics.length > 1 && total > 100) {
      return (
        `Static weight pins on ${layer} total ${total}% — if these rules ` +
        `match in the same pool, preview and apply will be blocked.`
      );
    }
  }
  return null;
}
